// Pure logic behind the Golfers step, kept out of the form markup.

// Shared by the Golfers step and its source picker, so both search boxes settle alike.
export const SEARCH_DEBOUNCE_MS = 350

export const GENDER_VALUES = [ 'M', 'F', 'unknown' ]

export const EMPTY_MEMBER_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  handicapIndex: '',
  gender: '',
  phone: '',
  ghin: '',
}

// index_for_save's own ceiling, as create_member stores the column verbatim and would keep a typo'd 184.
const MAX_HANDICAP_INDEX = 99.9

// Inverse of formatHandicap (plus positive, normal negative), accepting what index_for_save accepts.
export const parseHandicap = (display) => {
  if (display === null || display === undefined) { return null }
  const typed = String(display).trim()
  const bare = typed.toLowerCase().replace(/[\s[\]]/g, '')
  const str = bare.replace(',', '.').replace(/\D*$/, '')
  if (str === '') { return null }
  const plus = str[0] === '+' || str[0] === '-' || str[0] === 'p'
  const n = parseFloat(plus ? str.slice(1) : str)
  if (isNaN(n) || Math.abs(n) > MAX_HANDICAP_INDEX) { return null }
  const magnitude = Math.abs(parseFloat(n.toFixed(1)))
  return plus ? magnitude : -magnitude
}

// Custom fields are keyed by their configured name, whose casing varies per league.
export const getCustomField = (customFields, name) => {
  if (!customFields || !name) { return '' }
  const key = Object.keys(customFields).find((k) => k.toLowerCase() === name.toLowerCase())
  const value = key ? customFields[key] : ''
  // A null answer would seed a form field with null, which React rejects and .trim() throws on.
  return (value === null || value === undefined) ? '' : value
}

// 'F' as the roster stores it; anyone without an answer takes the men's tee, as the monolith does.
export const playsWomensTee = (player) =>
  String(getCustomField(player.custom_fields, 'gender')).toUpperCase() === 'F'

// One write per distinct tee, merged when both genders share one; `order` is the walk-on placement order.
export const teeGroupsFor = (players, { menTee, womenTee }) => {
  const femaleIds = []
  const maleIds = []
  players.forEach((player) => {
    const playerRoundId = player.player_round_id
    if (!playerRoundId) { return }
    if (playsWomensTee(player)) { femaleIds.push(playerRoundId) } else { maleIds.push(playerRoundId) }
  })

  const order = [ ...maleIds, ...femaleIds ]
  const groups = menTee === womenTee ? [ [ menTee, [ ...order ] ] ] : [ [ menTee, maleIds ], [ womenTee, femaleIds ] ]
  return { order, groups }
}

// Each gender's tee when every golfer of that gender holding one agrees; null when they differ (Individual).
export const sheetGenderTees = (players) => {
  const tees = { men: null, women: null }
  const agree = players.every((player) => {
    if (!player?.tee?.id) { return true }
    const gender = playsWomensTee(player) ? 'women' : 'men'
    tees[gender] = tees[gender] || player.tee
    return String(tees[gender].id) === String(player.tee.id)
  })
  return agree ? tees : null
}

// The roster comes from two calls: players placed in a foursome, and those enrolled but not placed.
export const enrolledPlayers = (foursomes, unplaced) => {
  const placed = foursomes.flatMap((group) => group.attributes?.players || [])
  return { placed, allEnrolled: [ ...placed, ...unplaced ] }
}

// The two rosters share no id (enrolment copies the member), so they match on e-mail, then on name.
const normalise = (value) => String(value ?? '').trim()
.toLowerCase()

// Leagues a field can be copied from: not the roster league nobody played, nor this event itself.
export const previousEventRows = (rows, currentLeagueId) => (rows || [])
  .filter((row) => row.attributes?.type !== 'group_roster' && String(row.id) !== String(currentLeagueId))

// A past event's v3 roster in the group roster's shape (`handle`, `usga_index`), so one filter serves both.
export const eventRosterRows = (members) => (members || []).map((member) => ({
  id: member.id,
  handle: member.name,
  first_name: member.first_name,
  last_name: member.last_name,
  email: member.email,
  usga_index: member.handicap?.handicap_index ?? null,
}))

export const addableRosterMembers = (rosterMembers, enrolled) => {
  const enrolledEmails = new Set(
    (enrolled || []).map((player) => normalise(player.email)).filter(Boolean),
  )
  // Every enrolled golfer, not only the ones without an e-mail: the tee sheet never sends one.
  const enrolledNames = new Set(
    (enrolled || []).map((player) => normalise(player.name)).filter(Boolean),
  )

  return (rosterMembers || [])
    .map((member) => ({
      memberId: String(member.id),
      name: member.handle || [ member.first_name, member.last_name ].filter(Boolean).join(' '),
      email: member.email || '',
      usgaIndex: member.usga_index ?? null,
    }))
    .filter((member) => {
      const email = normalise(member.email)
      if (email && enrolledEmails.has(email)) { return false }
      return !enrolledNames.has(normalise(member.name))
    })
}
